/**
 * Flows page showing scripts with their related nodes
 */
import React, { useState } from "react";
import { Link } from "react-router";
import {
  Search,
  ChevronRight,
  FileText,
  Workflow,
  GitBranch,
} from "lucide-react";
import { useScripts } from "../../hooks/useScripts";
import { useNodes } from "../../hooks/useNodes";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { Input } from "../../components/ui/Input";
import { Script } from "../../types/script";
import { Node } from "../../types/node";

interface FlowItemProps {
  script: Script;
  nodes: Node[];
  nodesLoading: boolean;
}

const FlowItem: React.FC<FlowItemProps> = ({ script, nodes, nodesLoading }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const scriptNodes = nodes.filter((node) => node.script_id === script.id);

  return (
    <Link to={`/flows/${script.id}`} className="block">
      <div className="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
              <GitBranch className="h-5 w-5 text-blue-500" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-1">
                  <h3 className="text-lg font-medium text-gray-900 truncate">
                    {script.name}
                  </h3>

                  {/* Version badge */}
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    v{script.version_number}
                  </span>

                  {/* Status badge */}
                  {script.status === "published" && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Published
                    </span>
                  )}
                  {script.status === "draft" && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Draft
                    </span>
                  )}
                  {script.status === "archived" && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                      Archived
                    </span>
                  )}
                </div>

                {script.description && (
                  <p className="text-sm text-gray-600 mb-2">
                    {script.description}
                  </p>
                )}

                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <div>Campaign ID: {script.campaign_id}</div>
                  <div>Updated {formatDate(script.updated_at)}</div>
                  {!nodesLoading && (
                    <div>
                      {scriptNodes.length} node
                      {scriptNodes.length !== 1 ? "s" : ""}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

const FlowsPage: React.FC = () => {
  const [search, setSearch] = useState("");

  // Fetch scripts
  const {
    data: scriptsResponse,
    isLoading: scriptsLoading,
    error: scriptsError,
    refetch: refetchScripts,
  } = useScripts({
    search: search || undefined,
    page_size: 100, // Maximum allowed by backend API
  });

  // Fetch all nodes for node count display
  const { nodes, loading: nodesLoading } = useNodes({
    pageSize: 100, // Maximum allowed by backend API
  });

  const scripts = scriptsResponse?.scripts || [];

  const handleSearch = (searchValue: string) => {
    setSearch(searchValue);
  };

  if (scriptsLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (scriptsError) {
    return (
      <ErrorMessage
        message="Failed to load flows. Please try again."
        onRetry={() => refetchScripts()}
        className="py-12"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Flows
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            View scripts and their associated nodes in a hierarchical structure
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Link
            to="/flows/new"
            className="btn-primary inline-flex items-center px-4 py-2 font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            create flow
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <Input
            type="text"
            placeholder="Search flows..."
            variant="shadow-sm"
            showLabel={false}
            className="pl-10"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        {/* Info text */}
        <div className="text-sm text-gray-500">
          Click on any flow to view its nodes
        </div>
      </div>

      {/* Scripts list */}
      {scripts.length === 0 ? (
        <div className="text-center py-12">
          <GitBranch className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No flows found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {search
              ? "Try adjusting your search terms."
              : "Create a script to see flows here."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Results summary */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing {scripts.length} flow{scripts.length !== 1 ? "s" : ""}
              {search && ` matching "${search}"`}
            </p>
          </div>

          {/* Flows list */}
          <div className="space-y-4">
            {scripts.map((script) => (
              <FlowItem
                key={script.id}
                script={script}
                nodes={nodes}
                nodesLoading={nodesLoading}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowsPage;
